import { defineStore } from "pinia";
import { IS_BP_MANAGER, ALLOWED_USER_ROLES } from "@/consts";
import { loginWithKeycloak, logoutFromKeycloak } from "@/helpers/auth/keycloak";
import api from "@/api";
import { type EmployeeExtended, type InstitutionBasic } from "@/extendedTypes";
import { sentryException } from "@/sentry";
import setPostLoginURL from "@/helpers/auth/setPostLoginURL";
import { IFrameMessage, LocalStorageKeys, SessionStorageKeys } from "@/enums";
import { globali18n } from "@/i18n";
import type { LegacyUserRole, SupportedLocales } from "@/types";
import { ref, watch } from "vue";
import type { LoggedUser, LoggedUserRole } from "@/graphql";
import sendMessageToOldManager from "@/helpers/routes/sendMessageToOldManager";
import emitter from "@/emitter";
import {
  User_Roles,
  Profile_Type,
  type Institution,
} from "@/graphql/generated-types";

type LoginListener = () => void;

export interface IAuthState {
  isAuthenticated: boolean;
  loggingOut: boolean;
  apiToken?: string | null;
  keycloakAccessToken?: string | null;
  keycloakRefreshToken?: string | null;
  idToken?: string | null;
  keycloakIdentity?: string | null;
  id?: string | null;
  currentInstitutionId: string | null;
  privacyPolicyAccepted: boolean;
  user?: LoggedUser;
  logoutPromptTimeout?: ReturnType<typeof setTimeout> | null;
}

type StoredAuthState = Partial<IAuthState> & { token?: string | null };

const DEFAULT_STATE: IAuthState = {
  isAuthenticated: false,
  loggingOut: false,
  apiToken: null,
  keycloakAccessToken: null,
  keycloakRefreshToken: null,
  idToken: null,
  keycloakIdentity: null,
  id: null,
  currentInstitutionId: null,
  privacyPolicyAccepted: false,
  user: undefined,
};

const loginListeners = ref<Array<LoginListener>>([]);
const initializations = new WeakMap<
  object,
  { promise: Promise<void>; pending: boolean }
>();

const getExistingInitialization = (
  state: IAuthState,
  isProfileReady: boolean,
): Promise<void> | undefined => {
  const existing = initializations.get(state);
  if (!existing) {
    return undefined;
  }

  // Share an ongoing login or profile request.
  if (existing.pending) {
    return existing.promise;
  }

  // A login redirect is already underway.
  if (!state.isAuthenticated) {
    return existing.promise;
  }

  // SPA navigation needs no new authentication.
  if (isProfileReady) {
    return existing.promise;
  }

  return undefined;
};

export const useAuthStore = defineStore("auth", {
  state: (): IAuthState => ({
    ...DEFAULT_STATE,
  }),
  getters: {
    isProfileReady(state): boolean {
      return (
        state.isAuthenticated &&
        !!state.user?.get?.id &&
        Array.isArray(state.user.get.roles) &&
        Array.isArray(state.user.institutions)
      );
    },
    institutionId(state): string {
      return state.currentInstitutionId || "";
    },
    currentInstitution(state): InstitutionBasic | null {
      const institution = this.profile?.institutions.find(
        (institution: InstitutionBasic) =>
          institution.id == state.currentInstitutionId,
      );

      return institution || null;
    },
    institutionLocale(): string {
      return this.currentInstitution?.locale || "da";
    },
    isSuperUser(state): boolean {
      return (state.user?.get?.roles || []).some(
        (item) =>
          item.role === User_Roles.Superuser &&
          item.institution.id === this.institutionId,
      );
    },
    roles(state): LoggedUserRole[] {
      if (!state.user) {
        return [];
      }

      return (state.user.get.roles || []).filter(
        (role) => role.institution.id === state.currentInstitutionId,
      );
    },
    legacyRoles(state): LegacyUserRole[] | undefined {
      // Only needed for permissions when iframing old manager
      return (state.user?.get?.roles || []).map((role) => {
        return {
          role: {
            name: role.role,
          },
        };
      });
    },
    rolesAsStringArray(state): string[] {
      return (state.user?.get.roles || []).map((item) =>
        item.role.toString().toLowerCase(),
      );
    },
    isSupport(): boolean {
      return !!this.rolesAsStringArray.includes("support");
    },
    firstEmployeeProfile(state): EmployeeExtended | undefined {
      if (!state.user || state.user?.get.profiles?.length === 0) {
        return;
      }

      if (state.user.get.profiles && state.user.get.profiles.length > 0) {
        const first = state.user.get.profiles[0] as EmployeeExtended;
        if (!first) {
          return;
        }

        first.email = state.user?.get.email;

        return first;
      }
    },
    profile(state): EmployeeExtended | null {
      if (!state.user) {
        return null;
      }

      const response = {
        ...(this.firstEmployeeProfile as EmployeeExtended),
        institutions: state.user.institutions as InstitutionBasic[],
        isSupportUser: this.roles.some((role) => role.role === "SUPPORT"),
      };

      // We should use the user ID, not the employee id
      response.userId = this.user?.get.id;

      // Setting the correct institution
      const institutionProfile = (state.user?.get.profiles || []).find(
        (item) =>
          item.type === Profile_Type.Employee &&
          item.institution.id === this.currentInstitutionId,
      );

      if (institutionProfile) {
        response.institution = institutionProfile.institution as Institution;
        response.id = institutionProfile.id;
        response.plannerFilter = institutionProfile.plannerFilter;
      }

      return response;
    },
    isNotInstitution(): boolean {
      if (!this.profile) {
        return true;
      }
      return !!(
        this.profile && this.profile.type.toLowerCase() !== "institution"
      );
    },
    profileType(): string | undefined {
      if (!this.profile) {
        return undefined;
      }

      return this.profile?.type?.toLowerCase();
    },
    showRedBorderForSupportUser() {
      // We don't need to show the red border for the support users, if the app
      // is iframed by the old manager.
      if (window.$features.iframe) {
        return false;
      }

      return this.isSupport;
    },
  },
  actions: {
    clearAuthState() {
      this.isAuthenticated = false;
      this.apiToken = null;
      this.keycloakAccessToken = null;
      this.keycloakRefreshToken = null;
      this.idToken = null;
      this.keycloakIdentity = null;
      this.id = null;
      this.currentInstitutionId = null;
      this.user = undefined;
    },
    async setLogin(
      apiToken: string,
      id: string,
      keycloakAccessToken: string,
      keycloakRefreshToken: string,
      idToken: string,
    ) {
      this.loggingOut = false;
      this.apiToken = apiToken;
      this.id = id;
      this.keycloakAccessToken = keycloakAccessToken;
      this.keycloakRefreshToken = keycloakRefreshToken;
      this.idToken = idToken;
      await this.updateProfile();
    },
    async setLocale() {
      // Wait little bit if there is a state update
      const timeout = setTimeout(() => {
        clearTimeout(timeout);
        // Setting the current locale value
        globali18n.locale.value = this.institutionLocale as SupportedLocales;
      }, 100);
    },
    async updateProfile() {
      const user = await api.user.getSelf();

      // Let's check if the user has access to the manager
      const hasAccess = (user.get.roles || []).some((item) =>
        ALLOWED_USER_ROLES.includes(item.role),
      );

      if (hasAccess === false) {
        // Check if user has ADMIN role - redirect to old manager

        const hasAdminRole = (user.get.roles || []).some(
          (item) => item.role === User_Roles.Admin,
        );

        if (hasAdminRole) {
          await this.logout(`${window.location.origin}/core-redirect`);
          return;
        }

        await this.logout(window.location.origin);
        return;
      }

      this.user = user;
      if (!window.$features.iframe && !this.currentInstitutionId) {
        this.currentInstitutionId =
          user.institutions?.[0]?.id ||
          this.firstEmployeeProfile?.institution?.id ||
          null;
      }

      // We shouldn't show a privacy policy on BP Manager
      if (IS_BP_MANAGER) {
        this.privacyPolicyAccepted = true;
      } else {
        this.privacyPolicyAccepted =
          this.user.get.settings?.policies?.managerPolicyAccepted || false;
      }

      this.isAuthenticated = true;
      this.id = this.user.get.id;
      this.updateToStorage();
      this.notifyLogin();
    },
    notifyLogin() {
      loginListeners.value.forEach((listener) => listener());
    },
    async setOldManagerLogin(token: string, institutionId: string) {
      this.apiToken = token;
      // Can be null, if sessionStorage.getItem('currentInstitution') in the old manager
      // is not yet populated.
      this.currentInstitutionId = institutionId;

      await this.updateProfile();
    },
    logoutPrompt() {
      if (this.logoutPromptTimeout) {
        return;
      }

      emitter.emit("logoutPrompt");
      this.logoutPromptTimeout = setTimeout(async () => {
        this.logout();
      }, 5000);
    },
    async logout(redirectUri?: string) {
      if (window.$features.iframe) {
        return;
      }

      const idToken = this.idToken;
      const keycloakAccessToken = this.keycloakAccessToken;
      const keycloakRefreshToken = this.keycloakRefreshToken;
      this.clearAuthState();
      this.loggingOut = true;
      this.clearStoredSession();
      if (this.logoutPromptTimeout) {
        clearTimeout(this.logoutPromptTimeout);
        this.logoutPromptTimeout = null;
      }

      await logoutFromKeycloak(
        {
          accessToken: keycloakAccessToken,
          refreshToken: keycloakRefreshToken,
          idToken,
        },
        redirectUri,
      );
    },
    clearStoredSession() {
      sessionStorage.removeItem(SessionStorageKeys.AuthModule);
      sessionStorage.removeItem(SessionStorageKeys.PostLoginURL);
      for (const key of [
        LocalStorageKeys.LoopbackAccessTokenId,
        LocalStorageKeys.LoopbackCurrentUserId,
      ]) {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      }
    },
    updateToStorage() {
      if (!this.isAuthenticated || this.loggingOut) {
        return;
      }
      this.setLocale();

      // Update in case it's different, as it can be initialized as null.
      // This seems to be the case right after login.
      const currentInstitutionId = new URLSearchParams(
        window.location.search,
      ).get("institution_id");
      if (
        currentInstitutionId &&
        currentInstitutionId != this.currentInstitutionId
      ) {
        this.currentInstitutionId = currentInstitutionId;
      }

      const data = JSON.stringify({
        isAuthenticated: this.isAuthenticated,
        apiToken: this.apiToken,
        keycloakIdentity: this.keycloakIdentity,
        id: this.id,
        currentInstitutionId: this.currentInstitutionId,
        privacyPolicyAccepted: this.privacyPolicyAccepted,
        user: this.user,
      });
      sessionStorage.setItem(SessionStorageKeys.AuthModule, data);
    },
    init(): Promise<void> {
      // Pinia action proxies change; the state object stays stable.
      const state = this.$state;
      const existing = getExistingInitialization(state, this.isProfileReady);
      if (existing) {
        return existing;
      }

      let startup: Promise<void>;

      if (this.isAuthenticated && !this.isProfileReady) {
        // Restore missing permissions without restarting Keycloak.
        startup = this.updateProfile();
      } else if (window.$features.iframe) {
        // Embedded authentication belongs to the parent manager.
        startup = this.restoreIframeSession();
      } else {
        // Standalone loads must authenticate with Keycloak.
        startup = this.initStandaloneSession();
      }

      const initialization = startup
        .catch((error: unknown) => {
          // Failed startup must not leave authenticated state.
          this.clearAuthState();

          if (window.$features.iframe) {
            // Preserve credentials owned by the parent manager.
            sessionStorage.removeItem(SessionStorageKeys.AuthModule);
          } else {
            // Remove the failed standalone session.
            this.clearStoredSession();
          }

          // Allow retry and report the failure to the caller.
          initializations.delete(state);
          sentryException(error);
          throw error;
        })
        .finally(() => {
          // Future calls can repair a missing profile.
          entry.pending = false;
        });

      // Cache before another caller can start the same work.
      const entry = { promise: initialization, pending: true };
      initializations.set(state, entry);
      return initialization;
    },
    async restoreIframeSession() {
      const data = sessionStorage.getItem(SessionStorageKeys.AuthModule);
      if (data) {
        const { token: legacyToken, ...parsed }: StoredAuthState =
          JSON.parse(data);
        const state: IAuthState = {
          ...DEFAULT_STATE,
          ...parsed,
          apiToken: parsed.apiToken || legacyToken || null,
          keycloakAccessToken: null,
          keycloakRefreshToken: null,
          idToken: null,
          keycloakIdentity: null,
        };

        // Let's update the current state
        this.$patch(state);

        // We might need to fetch the latest profile data because of default
        // values. To be able to fetch, we had to set the state first. Because
        // API requests need token.
        if (state.isAuthenticated && state.apiToken) {
          try {
            await this.updateProfile();
          } catch (error) {
            // If updateProfile fails (e.g., ACCESS_DENIED), clear session
            // User will be redirected to login by router guard
            sentryException(error);
            sessionStorage.removeItem(SessionStorageKeys.AuthModule);
            this.$patch(DEFAULT_STATE);
          }
        }

        this.updateToStorage();
      }
    },
    async initStandaloneSession() {
      const result = await loginWithKeycloak();
      if (!result.authenticated) {
        return;
      }

      const { accessToken, refreshToken, idToken, identity, isCallback } =
        result.session;
      const data = sessionStorage.getItem(SessionStorageKeys.AuthModule);
      let session: StoredAuthState | undefined;
      if (data) {
        try {
          session = JSON.parse(data);
        } catch (error) {
          sentryException(error);
          sessionStorage.removeItem(SessionStorageKeys.AuthModule);
        }
      }

      const apiToken = session?.apiToken || session?.token;
      if (
        session?.isAuthenticated &&
        !session.loggingOut &&
        typeof apiToken === "string" &&
        apiToken &&
        typeof session.id === "string" &&
        session.id &&
        session.keycloakIdentity === identity &&
        session.user?.get?.id === session.id &&
        Array.isArray(session.user?.institutions) &&
        Array.isArray(session.user?.get?.roles) &&
        Array.isArray(session.user?.get?.profiles)
      ) {
        this.$patch({
          ...DEFAULT_STATE,
          apiToken,
          id: session.id,
          user: session.user,
          currentInstitutionId:
            session.currentInstitutionId ||
            session.user.institutions[0]?.id ||
            null,
          privacyPolicyAccepted:
            session.privacyPolicyAccepted === true || IS_BP_MANAGER,
          isAuthenticated: true,
        });
        this.keycloakIdentity = identity;
        this.keycloakAccessToken = accessToken;
        this.keycloakRefreshToken = refreshToken;
        this.idToken = idToken;
        this.updateToStorage();
        this.notifyLogin();
        return;
      }

      this.clearAuthState();
      // Keep the callback's intended destination until the callback view consumes it.
      const postLoginURL = sessionStorage.getItem(
        SessionStorageKeys.PostLoginURL,
      );
      this.clearStoredSession();
      if (isCallback && postLoginURL) {
        sessionStorage.setItem(SessionStorageKeys.PostLoginURL, postLoginURL);
      }
      this.keycloakIdentity = identity;
      const response = await api.user.getToken(accessToken);
      if (!response?.accessToken || !response.id) {
        throw new Error("IBG did not return a valid API session");
      }
      await this.setLogin(
        response.accessToken,
        response.id,
        accessToken,
        refreshToken || "",
        idToken || "",
      );
    },
    async redirectLogin() {
      await this.init();
    },
    switchInstitution(newInstitutionId: string) {
      if (!this.profile?.institutions.length) {
        sentryException("No institutions connected to profile");
        return;
      }

      this.$patch({
        currentInstitutionId: newInstitutionId,
      });

      // Let's set the current URL as a flash message to use it in the callback
      setPostLoginURL();

      this.updateToStorage();

      this.setLocale();

      sendMessageToOldManager(IFrameMessage.InstitutionChanged, {
        newId: newInstitutionId,
      });

      window.location.reload();
    },
    addLoginListener(listener: LoginListener): void {
      loginListeners.value.push(listener);
    },
    removeLoginListener(listener: LoginListener): void {
      loginListeners.value = loginListeners.value.filter(
        (item) => item !== listener,
      );
    },
    async isSelfReady() {
      if (this.isProfileReady) {
        return true;
      }

      return new Promise<boolean>((resolve) => {
        const stop = watch(
          () => this.isProfileReady,
          (ready) => {
            if (ready) {
              stop();
              resolve(true);
            }
          },
        );
      });
    },
    updatePlannerFilterId(id: string) {
      const newProfileState = this.user?.get.profiles?.map((profile) => {
        if ((profile as EmployeeExtended).id === this.profile?.id) {
          (profile as EmployeeExtended).plannerFilter = { id };
        }

        return profile;
      });

      this.$patch({
        user: {
          get: {
            profiles: newProfileState,
          },
        },
      });
    },
  },
});
